import React, { useEffect, useState, Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import qs from 'qs';
import i18n from 'i18n-js';
import {
  Box,
  List,
  Divider,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import Can from 'components/Can';
import { Card } from 'components/UI';
import Loading from 'components/Loading';
import { uiActions } from 'state/modules/UI';
import StarIcon from '@material-ui/icons/Star';
import DeleteIcon from '@material-ui/icons/Delete';
import { PaymentActions } from 'state/modules/payment';
import {
  AddPaymentMethodModal,
  AddStripePaymentMethodModal,
  VerifyPaymentMethodModal,
} from 'components/Modals';
import { FETCH_STATE } from 'constants';
import { useHistory, useLocation } from 'react-router-dom';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { PaymentNumber, PaymentIcon, PaymentInfo } from '../PaymentComponents';
import { useStyles, listTextStyles, customCardStyles } from './PaymentMethodsWidget.styled';

const PaymentMethods = ({ modelId, accountId, type }) => {
  const classes = useStyles();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();

  const [params] = useState({
    page: 1,
    limit: 5,
  });
  const [openAddPaymentMethod, setOpenAddPaymentMethod] = useState(false);
  const [openVerifyPaymentMethod, setOpenVerifyPaymentMethod] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);

  const query = qs.parse(window.location.search.substring(1));
  const location = useLocation();
  const history = useHistory();

  const dispatch = useDispatch();

  const paymentMethods = useSelector((state) => state.payment.paymentMethods);
  const fetchPaymentMethods = useSelector((state) => state.payment.fetchPaymentMethods);
  const fetchAddPaymentMethod = useSelector((state) => state.payment.fetchAddPaymentMethod);
  const fetchUpdatePaymentMethod = useSelector((state) => state.payment.fetchUpdatePaymentMethod);
  const fetchDeletePaymentMethod = useSelector((state) => state.payment.fetchDeletePaymentMethod);

  const loading =
    isNaN(accountId) ||
    fetchPaymentMethods === FETCH_STATE.LOADING ||
    fetchAddPaymentMethod === FETCH_STATE.LOADING ||
    fetchUpdatePaymentMethod === FETCH_STATE.LOADING ||
    fetchDeletePaymentMethod === FETCH_STATE.LOADING;

  const refetch =
    fetchAddPaymentMethod === FETCH_STATE.DONE ||
    fetchUpdatePaymentMethod === FETCH_STATE.DONE ||
    fetchDeletePaymentMethod === FETCH_STATE.DONE;

  const removeParams = () => {
    const queryParams = new URLSearchParams(location.search);

    queryParams.delete('success');
    queryParams.delete('token');
    queryParams.delete('refId');
    history.replace({
      search: queryParams.toString(),
    });
  };

  useEffect(() => {
    if (query.success === 'false') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('common.generic_error'),
        })
      );
      removeParams();
    }
  }, [query.success, dispatch]); // eslint-disable-line

  /**
   * Get payment methods
   * @param {object} params
   */
  const getPaymentMethods = useCallback(
    (params) => {
      const payload = {
        ...params,
      };

      if (accountId) {
        payload.account_id = accountId;
      }

      dispatch(PaymentActions.fetchPaymentMethods(payload));
    },
    [accountId, dispatch]
  );

  const handleAddPaymentMethodModalOpen = () => {
    setOpenAddPaymentMethod((prevState) => !prevState);
  };

  const handleVerifyPaymentMethodModalOpen = () => {
    setCurrentPaymentId(null);
    setOpenVerifyPaymentMethod((prevState) => !prevState);
  };

  // Fetch payment methods on load once an account id is available (from user or organization)
  useEffect(() => {
    if (accountId) {
      getPaymentMethods(params);
    }
    if (query.token && query.success === 'true' && accountId) {
      dispatch(
        PaymentActions.fetchAddPaymentMethod({
          account_id: accountId,
          token: query?.refId,
          broker_type: 'KLK',
        })
      );
    }
  }, [accountId]); // eslint-disable-line

  // Fetch payment methods on refetch
  useEffect(() => {
    if (refetch) {
      getPaymentMethods(params);
    }
  }, [params, getPaymentMethods, refetch]);

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];

  if (type === 'dealers') {
    items.push({
      title: i18n.t('payments.add_payment_method'),
      onClick: () => handleAddPaymentMethodModalOpen(),
      can: { action: 'update', name: 'user_group' },
    });
  }

  if (type === 'organizations') {
    items.push({
      title: i18n.t('payments.add_payment_method'),
      onClick: () => handleAddPaymentMethodModalOpen(),
      can: { action: 'update', name: 'user_group' },
    });
  }

  const onDeleteClick = (item) => {
    if (item?.id) {
      dispatch(PaymentActions.fetchDeletePaymentMethod(item?.id));
    }
  };

  const onChangeDefault = (item) => {
    if (item?.id) {
      dispatch(PaymentActions.fetchUpdatePaymentMethod(item?.id, { default: !item.default }));
    }
  };

  const DefaultIcon = ({ isDefault }) => {
    return isDefault ? (
      <StarIcon style={{ color: '#FFB027', fontSize: 16, cursor: 'pointer' }} />
    ) : (
      <StarBorderIcon style={{ fontSize: 16, cursor: 'pointer' }} />
    );
  };

  return (
    <Card
      menuItems={items}
      title={i18n.t('payment_method.title')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.cardContentPadding}>
        {loading || refetch ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            {type === 'organizations' ? (
              <Typography variant="h3">
                <Box fontSize={13} mb={3}>
                  {i18n.t('organization.payment_method_description')}
                </Box>
              </Typography>
            ) : null}
            {paymentMethods?.length > 0 ? (
              <List data-cy="payment_methods_added">
                {paymentMethods?.map((val, index) => (
                  <Fragment key={index}>
                    <Divider />
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <PaymentIcon type={val?.payment_type} />
                      </ListItemIcon>
                      <ListItemText
                        classes={listClasses}
                        primary={
                          <PaymentNumber data={{ ...(val?.card || val?.ach || {}), ...val }} />
                        }
                        secondary={
                          <PaymentInfo
                            setCurrentPaymentId={setCurrentPaymentId}
                            data={{ ...(val?.card || val?.ach || {}), ...val }}
                            setOpenVerifyPaymentMethod={setOpenVerifyPaymentMethod}
                          />
                        }
                      />
                      {paymentMethods?.length > 1 && (
                        <Box fontSize={13} style={{ maxWidth: '2.5rem' }}>
                          <Can I="update" on="payment_method">
                            <IconButton onClick={() => onChangeDefault(val)}>
                              <DefaultIcon isDefault={val?.default} />
                            </IconButton>
                          </Can>
                          <Can I="delete" on="payment_method">
                            <IconButton
                              data-cy="btn-delete-payment-method"
                              disabled={fetchDeletePaymentMethod === FETCH_STATE.LOADING}
                              onClick={() => onDeleteClick(val)}
                            >
                              <DeleteIcon
                                style={{ color: '#8E8E93', fontSize: 16, cursor: 'pointer' }}
                              />
                            </IconButton>
                          </Can>
                        </Box>
                      )}
                    </ListItem>
                  </Fragment>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary" style={{ lineHeight: 1 }}>
                <Box fontSize={13} component="span" fontWeight="medium">
                  {i18n.t('payment_method.empty')}
                </Box>
              </Typography>
            )}
          </>
        )}
      </Box>
      {openAddPaymentMethod && type === 'dealers' ? (
        <AddPaymentMethodModal
          open={true}
          type={type}
          modelId={modelId}
          accountId={accountId}
          handleClick={handleAddPaymentMethodModalOpen}
        />
      ) : null}
      {openAddPaymentMethod && type === 'organizations' ? (
        <AddStripePaymentMethodModal
          open={true}
          handleClick={handleAddPaymentMethodModalOpen}
          accountId={accountId}
        />
      ) : null}
      {openVerifyPaymentMethod && type === 'organizations' ? (
        <VerifyPaymentMethodModal
          paymentId={currentPaymentId}
          open={true}
          handleClick={handleVerifyPaymentMethodModalOpen}
          accountId={accountId}
        />
      ) : null}
    </Card>
  );
};

export { PaymentMethods };
